import { useMemo } from 'react';
import { Typography } from '@mui/material';
import { css } from '@emotion/react';
import { useParams } from 'react-router';
import { colors } from '../../theme/colors';
import { Loader } from '../../components/Loader/Loader';
import { useSourcingProfile } from './hooks/useSourcingProfile';
import { useSourcingCompanyNews } from './hooks/useSourcingCompanyNews';
import { useSourcingCompanySummary } from './hooks/useSourcingCompanySummary';
import { useSourcingCompanyLists } from './hooks/useSourcingCompanyLists';
import { SourcingProfileHeader, SourcingProfileHeaderSkeleton } from './profile/SourcingProfileHeader';
import { TrafficStatsRow, TrafficStatsRowSkeleton } from './profile/TrafficStatsRow';
import { OverviewSidebar } from './profile/OverviewSidebar';
import { panelSurfaceCss } from './profile/panelStyles';
import { ForesightSummaryPanel } from './profile/ForesightSummaryPanel';
import { FundingEvolutionPanel } from './profile/FundingEvolutionPanel';
import { FinancingHistoryPanel } from './profile/FinancingHistoryPanel';
import { HighlightsPanel } from './profile/HighlightsPanel';
import { KeyPeoplePanel } from './profile/KeyPeoplePanel';
import { ContactsWebPanel } from './profile/ContactsWebPanel';
import { RecentNewsPanel } from './profile/RecentNewsPanel';

// Figma page gradient — subtle radial from top-left, laid over the neutral
// background so translucent panels read correctly on top.
//
// NOTE: `neutral[5]` is #F9FAFA, so a gradient from rgba(249,250,250,...) is
// effectively invisible. We instead fade from a slightly primary-tinted wash
// (`primary[5]` #F8F8FF) to the neutral base so the warmth is visible behind
// panels, between them and in the gutter.
const pageGradientCss = css`
  background:
    radial-gradient(100% 204.54% at 0% 0%, ${colors.primary[5]} 0%, rgba(255, 255, 255, 0) 100%),
    ${colors.neutral[5]};
`;

const pageWrapperCss = css`
  ${pageGradientCss}
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

// The scroll area also carries the gradient so it stays visible as the user
// scrolls below the initial viewport (otherwise the fixed page bg only paints
// the portion above the fold).
const scrollAreaCss = css`
  ${pageGradientCss}
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const trafficStatsWrapperCss = css`
  padding-top: 12px;
  flex-shrink: 0;
`;

const bodyWrapperCss = css`
  display: flex;
  gap: 12px;
  padding: 12px;
  align-items: flex-start;
  flex-shrink: 0;
`;

/** Stretches the full height of the body content so the Digest gradient fills the column end-to-end. */
const sidebarColumnCss = css`
  width: 340px;
  flex-shrink: 0;
  align-self: stretch;
`;

const mainContentCss = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Flex-fills the remaining scroll-area height (after the traction skeleton) so
// the shared Loader stays centered in the visible viewport without introducing
// an extra scrollbar during loading.
const loadingCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
`;

const errorCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.neutral[60]};
`;

export function SourcingProfilePage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { data, isLoading, isError } = useSourcingProfile(companyId ?? '');
  // News is loaded independently so a slow / failing news call doesn't block
  // the rest of the profile. We treat any failure as "no news" and hide the
  // panel rather than surface an error — same UX contract the empty state
  // already provides.
  const { data: news } = useSourcingCompanyNews(companyId ?? '');
  // Foresight AI summary is also independent — the LLM call is slow and may
  // legitimately return an empty list. The page hides the panel either way.
  const { summary: foresightSummary } = useSourcingCompanySummary(companyId ?? '');
  // Saved-lists membership feeds the header's `Added to N lists` text and
  // the `AddToListFromProfileAction` picker (so already-saved lists are
  // hidden from the dropdown). Failures degrade silently to "no matches".
  const { data: companyLists } = useSourcingCompanyLists(companyId ?? '');

  // Leadership surfaces founders AND non-founder executives (e.g. a hired
  // CEO). The adapter partitions them into `founderPeople` / `keyPeople`
  // on purpose so future sections can split them, but we currently render
  // a single panel — founders first, then everyone else.
  const leadershipPeople = useMemo(() => (data ? [...data.founderPeople, ...data.keyPeople] : []), [data]);

  // Overlay the live `listsCount` from the membership lookup on top of the
  // adapter's value (which is `undefined` for real API data — only mocks
  // populate it). Done at the page level so the header stays presentational.
  const headerData = useMemo(
    () => (data ? { ...data, listsCount: companyLists?.lists.length ?? data.listsCount } : data),
    [data, companyLists]
  );

  // No valid `:companyId` segment — surface an explicit error rather than
  // silently falling back to a hard-coded profile (previous default was
  // `'anthropic'`, which masked routing bugs).
  if (!companyId) {
    return (
      <div css={pageWrapperCss}>
        <div css={errorCss}>
          <Typography sx={{ fontSize: 13, color: colors.neutral[60] }}>No company selected.</Typography>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div css={pageWrapperCss}>
        <SourcingProfileHeaderSkeleton />
        <div css={scrollAreaCss}>
          <div css={trafficStatsWrapperCss}>
            <TrafficStatsRowSkeleton />
          </div>
          <div css={loadingCss}>
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div css={pageWrapperCss}>
        <div css={errorCss}>
          <Typography sx={{ fontSize: 13, color: colors.neutral[60] }}>
            Failed to load sourcing profile.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div css={pageWrapperCss}>
      <SourcingProfileHeader data={headerData!} companyId={companyId} />
      <div css={scrollAreaCss}>
        <div css={trafficStatsWrapperCss}>
          <TrafficStatsRow stats={data.trafficStats} />
        </div>
        <div css={bodyWrapperCss}>
          <div css={[panelSurfaceCss, sidebarColumnCss]}>
            <OverviewSidebar data={data} />
          </div>
          <div css={mainContentCss}>
            {foresightSummary.length > 0 && <ForesightSummaryPanel summary={foresightSummary} />}
            <FundingEvolutionPanel data={data.fundingEvolution} webTrafficData={data.webTrafficEvolution} />
            {data.fundingRounds.length > 0 && <FinancingHistoryPanel rounds={data.fundingRounds} />}
            {news != null && news.length > 0 && <RecentNewsPanel news={news} />}
            <ContactsWebPanel data={data} />
            {data.highlights.length > 0 && <HighlightsPanel highlights={data.highlights} />}
            {leadershipPeople.length > 0 && <KeyPeoplePanel people={leadershipPeople} title='Leadership' />}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The profile page's frame, shared with the frosted sample of this screen. */
export const sourcingProfileLayoutCss = {
  page: pageWrapperCss,
  scrollArea: scrollAreaCss,
  trafficStats: trafficStatsWrapperCss,
  body: bodyWrapperCss,
  sidebar: [panelSurfaceCss, sidebarColumnCss],
  main: mainContentCss,
} as const;
